import { useStoreState } from "pullstate";
import { subscribeUserNotification } from "../../utils/pushNotification";
import InApppAlert from "../InAppAlert";
import Store from "../../utils/Store";
import { get, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import constants from "../../utils/constants";
import { differenceInMilliseconds } from "date-fns";
import { getFCMToken } from "../../utils";
import { t } from "i18next";
import useSaveNotificationSettings from "../NotificationSettings/services/useSaveNotificationSettings";
import Toast from "../Toast";
import Loader from "../Loader";
import { useRouter } from "next/router";
import LoggedInToken from "../../utils/LoggedInToken";
import { trackEvent } from "../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../utils/mixpanelEvents";
import { useAuth } from "@qure/qure-auth";

const NotificationSubscription = () => {
  const isMobileApp = window?.ReactNativeWebView;
  const router = useRouter();
  const [isIgnored, setIgnored] = useState(
    !!localStorage.getItem(constants.PERSISTED.notificationPromptDismiss)
  );
  const saveNotificationSettings = useSaveNotificationSettings();
  const { userInfo, refetchProfile } = useAuth();

  const { fcmToken, RNfcmToken, isRNAppStateChange, isNotificationSubscribing } =
    useStoreState(Store, (s) => ({
      RNfcmToken: s.RNfcmToken,
      isRNAppStateChange: s.isRNAppStateChange,
      RNAppVersion: s.RNAppVersion,
      fcmToken: s.fcmToken,
      isNotificationSubscribing: s.isNotificationSubscribing,
    }));
  const token = RNfcmToken || fcmToken;
  const isLoggedIn = get(userInfo, ["authenticated"], false);
  const hasPermission =
    (isMobileApp && (!!RNfcmToken || RNfcmToken === "")) ||
    window?.Notification?.permission === "granted";
  const isNotificationsOn =
    !isLoggedIn || !get(userInfo, ["settings", "notification_snoozed"], false);
  const isSubscribed =
    isNotificationSubscribing ||
    (!isLoggedIn && hasPermission) ||
    (!token && hasPermission) ||
    get(userInfo, ["subscription_tokens"], []).some(
      (tokenObj: Record<string, any>) => get(tokenObj, ["token"], "") === token
    );

  let alertTimeout: NodeJS.Timeout;
  useEffect(() => {
    if (isLoggedIn) {
      setDefaultFCMTokens();
      setAlertTimer();
      if (LoggedInToken.isLoginProgress()) {
        LoggedInToken.removeLogInProgress();
        updateSubscription();
      }
    }
    return () => {
      clearTimeout(alertTimeout);
    };
  }, [isLoggedIn]);

  // when user change the notification permission of app from device settngs
  useEffect(() => {
    if (isMobileApp && isRNAppStateChange && isLoggedIn) {
      const _hasPermission = !!RNfcmToken;
      if (!hasPermission && _hasPermission) {
        updateSubscription();
      }
    }
  }, [RNfcmToken, isRNAppStateChange]);

  // To nudge users every 6  hours if user has ignored the popup and not enabled notification yet
  const setAlertTimer = () => {
    if (hasPermission && isSubscribed && isNotificationsOn) {
      return localStorage.removeItem(constants.PERSISTED.notificationPromptDismiss);
    }
    const timeStr = localStorage.getItem(constants.PERSISTED.notificationPromptDismiss);
    if (timeStr) {
      const ignoredTime = new Date(timeStr);
      ignoredTime.setHours(ignoredTime.getHours() + 12);
      const remainingTime = differenceInMilliseconds(ignoredTime, new Date());

      alertTimeout = setTimeout(() => {
        clearTimeout(alertTimeout);
        localStorage.removeItem(constants.PERSISTED.notificationPromptDismiss);
        setIgnored(false);
      }, remainingTime);
    }
  };

  // check if user is subscribed for notification
  const setDefaultFCMTokens = async () => {
    if (window?.Notification?.permission === "granted") {
      const fcmToken = await getFCMToken();
      Store.update((s) => {
        s.fcmToken = fcmToken;
      });
    }
  };

  const onIgnore = () => {
    if (isNotificationSubscribing) {
      return;
    }
    clearTimeout(alertTimeout);
    localStorage.setItem(
      constants.PERSISTED.notificationPromptDismiss,
      new Date().toString()
    );
    setIgnored(true);
    setAlertTimer();
    trackEvent(MIXPANEL_EVENTS.IGNORE_NOTIFICATION_FROM_NUDGE, {
      hasPermission,
      isSubscribed,
      isNotificationsOn,
    });
  };

  const updateSubscription = async () => {
    Store.update((s) => {
      s.isNotificationSubscribing = true;
    });
    const subscription = await subscribeUserNotification(RNfcmToken);
    if (subscription) {
      await refetchProfile();
    }
    Store.update((s) => {
      s.isNotificationSubscribing = false;
    });
  };

  const enableGlobalNotifications = async () => {
    try {
      await saveNotificationSettings.mutateAsync({ notification_snoozed: false });
      refetchProfile();
    } catch (err) {
      Toast.error("Something went wrong. Please try again");
    }
  };

  const onEnableNotification = () => {
    if (isNotificationSubscribing) {
      return;
    }
    if (isMobileApp && !hasPermission) {
      const data = {
        event: constants.RN_COMM_CONST.OPEN_APP_SETTINGS,
        data: null,
      };
      window?.ReactNativeWebView?.postMessage(JSON.stringify(data));
    } else if (isSubscribed && !isNotificationsOn) {
      enableGlobalNotifications();
    } else {
      updateSubscription();
    }
    trackEvent(MIXPANEL_EVENTS.ENABLE_NOTIFICATION_FROM_NUDGE, {
      hasPermission,
      isSubscribed,
      isNotificationsOn,
    });
  };

  return (
    <InApppAlert
      show={
        !isNotificationSubscribing &&
        router.pathname != "/open-app" &&
        isLoggedIn &&
        !isIgnored &&
        (!hasPermission || !isSubscribed || !isNotificationsOn)
      }
      message={
        !hasPermission || !isSubscribed
          ? t("notification_modal.no_notifications_setting")
          : t("notification_modal.no_notifications")
      }
      messageClassName="!text-gray-800"
      primaryCTA={
        <a className="text-teal-500 cursor-pointer" onClick={onEnableNotification}>
          {isMobileApp && !hasPermission
            ? t("notification_modal.enable_settings")
            : t("notification_modal.turn_on_notification")}
        </a>
      }
      secondaryCTA={
        <a className="text-gray-600 cursor-pointer" onClick={onIgnore}>
          {t("notification_modal.ignore")}
        </a>
      }
      className=""
      icon={
        isNotificationSubscribing ? (
          <Loader size="medium" className="border-teal-200" />
        ) : (
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.1437 4.25V5.50719C26.9989 6.31648 30.7156 10.7328 30.7156 16.0625V18.4109C30.7156 21.6031 31.7536 24.7039 33.6488 27.2L34.6466 28.5078C35.035 29.0141 35.1086 29.7102 34.8408 30.2938C34.5729 30.8773 34.0104 31.25 33.3943 31.25H6.60723C5.98912 31.25 5.42626 30.8773 5.15866 30.2938C4.89099 29.7102 4.96626 29.0141 5.35226 28.5078L6.35075 27.2C8.25062 24.7039 9.28594 21.6031 9.28594 18.4109V16.0625C9.28594 10.7328 13.0026 6.31648 17.8578 5.50719V4.25C17.8578 3.00758 18.8154 2 20.0008 2C21.1861 2 22.1437 3.00758 22.1437 4.25ZM19.465 8.75C15.6211 8.75 12.5004 12.0266 12.5004 16.0625V18.4109C12.5004 21.7789 11.5709 25.0625 9.84244 27.875H30.1597C28.432 25.0625 27.5011 21.7789 27.5011 18.4109V16.0625C27.5011 12.0266 24.3804 8.75 20.5365 8.75H19.465ZM24.2867 33.5C24.2867 34.632 23.838 35.8414 23.0344 36.6852C22.2308 37.5289 21.0789 38 20.0008 38C18.8623 38 17.7707 37.5289 16.9671 36.6852C16.1635 35.8414 15.7148 34.632 15.7148 33.5H24.2867Z"
              fill="#00D0CD"
            />
          </svg>
        )
      }
    />
  );
};

export default NotificationSubscription;
